import { observable } from '@legendapp/state';
import { syncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { createSyncCreate, isAuthed$ } from './syncHelpers';
import { sharedListIds$ } from './listMembers$';

// Article-Lists junction table store (local-first)
// Maps articles to lists (many-to-many relationship)
// RLS handles visibility: only list members see associations for their lists
//
// NOTE: This table uses HARD deletes (no deleted/updated_at columns).
// We cannot use changesSince or fieldDeleted from defaultSyncConfig.
// Instead, we do full syncs and rely on realtime for live updates.
export const articleLists$ = observable(syncedSupabase({
  supabase,
  collection: 'reedle_article_lists',
  // No user_id filter needed — RLS policies use is_list_member(list_id)
  // This means we get all article-list associations for lists the user is a member of
  select: (from) => from.select('id, article_id, list_id, added_by, created_at'),
  // Custom create with duplicate key handling (upsert on article_id + list_id)
  // No update handler - junction rows are immutable (add/remove only)
  // IMPORTANT: Don't set created_at locally in addArticleToList, or Legend-State
  // routes to update handler (fieldCreatedAt determines new vs existing).
  // See: https://www.legendapp.com/open-source/state/v3/sync/supabase
  create: createSyncCreate('reedle_article_lists', { onConflict: 'article_id,list_id' }),
  // Hard delete handler - actually removes rows from database
  // Without this, deletions only happen locally and don't sync
  delete: async ({ id }) => {
    const { error } = await supabase
      .from('reedle_article_lists')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
  // Realtime for live updates when list members add/remove articles
  realtime: true,
  // Timestamp field for ordering
  fieldCreatedAt: 'created_at',
  // Delay remote sync until auth is ready (matches defaultSyncConfig pattern)
  waitFor: isAuthed$,
  // Retry config
  retry: {
    times: 3,
    backoff: 'exponential',
    maxDelay: 30000,
  },
  // Persist locally
  persist: {
    name: 'reedle-article-lists',
    plugin: ObservablePersistLocalStorage,
    retrySync: true,
  },
}));

// --- Helper Functions ---

/**
 * Get all list IDs for an article
 * @param {string} articleId
 * @returns {string[]} Array of list IDs
 */
export const getArticleListIds = (articleId) => {
  const obj = articleLists$.get() || {};
  const listIds = [];
  for (const entry of Object.values(obj)) {
    if (entry?.article_id === articleId) {
      listIds.push(entry.list_id);
    }
  }
  return listIds;
};

/**
 * Check if an article is in a specific list
 * @param {string} articleId
 * @param {string} listId
 * @returns {boolean}
 */
export const isArticleInList = (articleId, listId) => {
  const obj = articleLists$.get() || {};
  return Object.values(obj).some(
    entry => entry?.article_id === articleId && entry?.list_id === listId
  );
};

/**
 * Get the junction row ID for a specific article-list pair
 * @param {string} articleId
 * @param {string} listId
 * @returns {string|null} The junction row ID, or null
 */
const getArticleListEntryId = (articleId, listId) => {
  const obj = articleLists$.get() || {};
  for (const [id, entry] of Object.entries(obj)) {
    if (entry?.article_id === articleId && entry?.list_id === listId) {
      return id;
    }
  }
  return null;
};

/**
 * Add an article to a list (creates junction row)
 * If already in the list, this is a no-op.
 * The DB trigger will update articles.list_id automatically.
 * @param {string} articleId
 * @param {string} listId
 */
export const addArticleToList = (articleId, listId) => {
  if (!articleId || !listId) return;
  const userId = auth$.user.get()?.id;
  if (!userId) return;

  // Check if already in this list
  if (isArticleInList(articleId, listId)) return;

  const id = uuidv4();
  // Don't set created_at - let server set it via DEFAULT.
  // Legend-State uses fieldCreatedAt to determine if record is new.
  articleLists$[id].set({
    id,
    article_id: articleId,
    list_id: listId,
    added_by: userId,
  });
};

/**
 * Remove an article from a list (deletes junction row)
 * The DB trigger will update articles.list_id automatically.
 * @param {string} articleId
 * @param {string} listId
 */
export const removeArticleFromList = (articleId, listId) => {
  if (!articleId || !listId) return;

  const entryId = getArticleListEntryId(articleId, listId);
  if (!entryId) return;

  // Hard delete the junction row
  // For syncedSupabase without fieldDeleted, we delete from the observable
  // and the sync layer will issue a DELETE to Supabase
  articleLists$[entryId].delete();
};

/**
 * Remove an article from ALL lists (deletes all junction rows for this article)
 * Used when deleting an article to clean up list associations
 * @param {string} articleId
 */
export const removeArticleFromAllLists = (articleId) => {
  if (!articleId) return;

  const obj = articleLists$.get() || {};
  for (const [entryId, entry] of Object.entries(obj)) {
    if (entry?.article_id === articleId) {
      articleLists$[entryId].delete();
    }
  }
};

/**
 * Get article count for a specific list (from junction table)
 * @param {string} listId
 * @returns {number}
 */
export const getArticleCountForList = (listId) => {
  const obj = articleLists$.get() || {};
  let count = 0;
  for (const entry of Object.values(obj)) {
    if (entry?.list_id === listId) {
      count++;
    }
  }
  return count;
};

/**
 * Get all article IDs in a specific list
 * @param {string} listId
 * @returns {string[]}
 */
export const getArticleIdsInList = (listId) => {
  const obj = articleLists$.get() || {};
  const articleIds = [];
  for (const entry of Object.values(obj)) {
    if (entry?.list_id === listId) {
      articleIds.push(entry.article_id);
    }
  }
  return articleIds;
};

/**
 * Check if article should be hidden by "hide shared" filter
 * Returns true if article is in any shared list AND not in any non-shared list
 * (If article is in both shared and personal lists, it's not considered "shared")
 * @param {string} articleId
 * @returns {boolean}
 */
export const isArticleShared = (articleId) => {
  const listIds = getArticleListIds(articleId);
  if (listIds.length === 0) return false; // Inbox = not shared

  const sharedIds = sharedListIds$.get() || [];
  const sharedIdsSet = new Set(sharedIds);

  const inSharedList = listIds.some(id => sharedIdsSet.has(id));
  const inNonSharedList = listIds.some(id => !sharedIdsSet.has(id));

  return inSharedList && !inNonSharedList;
};
